import { CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import Blade from '@/components/ui/Blade/Blade';
import Button from '@/components/ui/Button';
import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import { RelatedSupportRequestDtoIn } from '@/models/in/RelatedSupportRequestDtoIn';
import { DocumentWithDetailsDtoIn } from '@/models/in/DocumentWithDetailsDtoIn';
import { DocumentStatus } from '@/models/common/DocumentStatus';
import { getStatusColor } from '@/helpers/requestStatus';
import { getShortRequestId } from '@/helpers/requestIdHelper';
import { getApproveStatus, getRejectStatus } from '@/helpers/supportRequestDecision';
import { getExpirationStatus, getExpirationStatusColor } from '@/helpers/contactClassificationExpiration';
import { RelationshipStatus } from '@/models/common/RelationshipStatus';
import { getStatusColorSets } from '@/helpers/cssHelpers';
import { DOCUMENT_VALIDITY_MONTHS, resolveRequestDocumentDate } from '@/helpers/supportDocumentDate';
import { AUTO_OFFICE_COMMENTS_VALUES } from '@/constants/officeComments';
import donation from '@/assets/icons/donation.svg';
import documentsIcon from '@/assets/icons/documents.svg';
import editIcon from '@/assets/icons/edit.svg';
import SupportRequestType from '@/models/common/SupportRequestType';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';
import { PaymentInfoStatus } from '@/models/common/PaymentInfoStatus';
import { KollelDetails } from '@/models/common/KollelDetails';
import { FundExpenseDetails } from '@/models/FundExpenseDetails';
import AllowedFor from '@/models/common/AllowedFor';
import { SupplierTaxApprovalStatus } from '@/models/in/SupplierFullDtoIn';
import { getSupplierDisplayDetails, getPaymentBankDetails } from '@/helpers/supplierDisplayHelper';
import { openSupplierTaxApprovalDocument } from '@/helpers/openSupplierTaxApprovalDocument';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';
import CompletionUploadSection from '@/components/CompletionUploadSection/CompletionUploadSection';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import alertIcon from '@/assets/icons/alert-circle.svg';
import styles from './AdditionalInfo.module.css';

interface AdditionalInfoProps {
  request: ManagementSupportRequestDtoIn;
  documents: DocumentWithDetailsDtoIn[];
  isLoadingDocs: boolean;
  handleXclick: () => void;
  onChangeBankAccount?: () => void;
  onOpenOfficeComments?: () => void;
  onEditRequestedForDate?: () => void;
  onRequestCompletion?: () => void;
  /** HAT-1403: management attaches documents in any status; the status is not changed. */
  uploadDocumentsEndpoint?: string;
  onDocumentsUploaded?: () => void;
  onChangeGoalType?: () => void;
  onConvertToFundExpense?: () => void;
  relatedSupportRequests?: RelatedSupportRequestDtoIn[];
  isLoadingRelatedSupportRequests?: boolean;
  /**
   * HAT-1391: the blade covers the table's status column, so the same status moves are offered
   * here. The page routes them through its own status-change flow (confirmation dialog, supplier
   * gate, request-completion dialog), exactly as the column's dropdown does.
   */
  onStatusChange?: (newStatus: string) => void;
  isStatusChangeDisabled?: boolean;
}

const AdditionalInfo = ({ request, documents, isLoadingDocs, handleXclick, onChangeBankAccount, onOpenOfficeComments, onEditRequestedForDate, onRequestCompletion, uploadDocumentsEndpoint, onDocumentsUploaded, onChangeGoalType, onConvertToFundExpense, relatedSupportRequests, isLoadingRelatedSupportRequests, onStatusChange, isStatusChangeDisabled }: AdditionalInfoProps) => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuthContext();
  const statusColorSets = useMemo(() => getStatusColorSets(), []);
  // SPR-10015: the document's date and whether it is within the validity window
  const documentDate = request ? resolveRequestDocumentDate(request) : null;

  const displayStatus = request.isInInitialStatusProcess ? request.initialStatus : request.status;
  const displayAmount = request.approvedAmount;

  const isAmountNegative = displayAmount < 0;
  const amountColors = isAmountNegative
    ? 'text-(--font-primary) bg-(--surface-primary-lowest)'
    : 'text-(--font-tertiary) bg-(--surface-tertiary-low)';

  const formatDate = (date: string) => {
    return Intl.DateTimeFormat('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(date));
  };

  const formatDateTime = (date: string) => {
    const utcDate = new Date(date + (date.endsWith('Z') ? '' : 'Z'));
    return Intl.DateTimeFormat('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Jerusalem',
    }).format(utcDate);
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString(i18n.language, {
      style: 'currency',
      currency: 'ILS',
    });
  };

  const getRequestTypeDisplay = () => {
    if (request.type === SupportRequestType.OneTime) {
      return t('oneTime');
    }
    if (request.isInInitialStatusProcess) {
      return `${request.multiRequestTotalCount} ${t('months')}`;
    }
    return `${request.multiRequestCounter}/${request.multiRequestTotalCount}`;
  };

  // Header recipient label - beneficiary requests show the supported person; fund-expense
  // requests don't have a beneficiary contact, so we fall back to the supplier name.
  const isFundExpense = request.goal?.allowedFor === AllowedFor.Fund;
  const recipientLabel = isFundExpense
    ? request.supplier?.name?.trim() || t('supplier')
    : `${request.beneficiaryContact?.firstName ?? ''} ${request.beneficiaryContact?.lastName ?? ''}`.trim();

  const headerTop = (
    <span className="body-lr-regular">
      <span className="body-lr-bold">{getShortRequestId(request.multiRequestCorrelationId)}</span>
      {' | '}
      {t(request.goal?.name || '')}
      {' - '}
      <span className="body-lr-link">{recipientLabel}</span>
    </span>
  );

  const headerBottom = (
    <>
      <div className="flex gap-2">
        <span className={`${styles.amount} body-sm-medium ${amountColors}`}>
          <img src={donation} alt="" />
          {formatCurrency(displayAmount)}
        </span>
        <span className={`${styles.status} body-sm-medium`}>
          <span
            className={styles['status-color']}
            style={{ '--status-color': getStatusColor(String(displayStatus)) } as CSSProperties}
          ></span>
          {t(String(displayStatus))}
        </span>
      </div>
      <span className="body-sm-regular" style={{ direction: 'ltr' }}>
        {request.requestedForDates.length === 1
          ? formatDate(request.requestedForDates[0])
          : `${formatDate(request.requestedForDates[0])} - ${formatDate(request.requestedForDates[request.requestedForDates.length - 1])}`
        }
      </span>
    </>
  );

  const officeCommentsCount = request.officeComments?.length || 0;

  const approveStatus = getApproveStatus(request);
  const rejectStatus = getRejectStatus(request);
  const otherStatuses = (request.allowedNextStatuses ?? []).filter((s) => s !== String(displayStatus));
  const showStatusActions = !!onStatusChange && otherStatuses.length > 0;

  return (
    <Blade handleXclick={handleXclick} headerTop={headerTop} headerBottom={headerBottom} width="520px">
      {showStatusActions && (
        <div className={styles.statusActions}>
          {approveStatus && (
            <Button variant="primary" className={styles.statusActionButton} disabled={isStatusChangeDisabled} onClick={() => onStatusChange?.(approveStatus)}>
              {t('supportRequestsManagement.approveRequest')}
            </Button>
          )}
          {rejectStatus && (
            <Button variant="outline" disabled={isStatusChangeDisabled} onClick={() => onStatusChange?.(rejectStatus)}>
              {t('supportRequestsManagement.rejectRequest')}
            </Button>
          )}
          {/* Every move the table's status dropdown offers, so nothing has to be done outside. The
              value stays on the placeholder: the page opens a dialog, and cancelling it must leave
              this control as it was. */}
          <select
            className={styles.statusSelect}
            value=""
            disabled={isStatusChangeDisabled}
            aria-label={t('supportRequestsManagement.changeStatus')}
            onChange={(e) => e.target.value && onStatusChange?.(e.target.value)}
          >
            <option value="" disabled>
              {t('supportRequestsManagement.changeStatus')}
            </option>
            {otherStatuses.map((nextStatus) => (
              <option key={nextStatus} value={nextStatus}>
                {t(nextStatus)}
              </option>
            ))}
          </select>
        </div>
      )}
      <Tabs
        defaultValue="documents"
        style={{ direction: 'inherit', ...(showStatusActions ? { '--status-actions-height': '57px' } : {}) } as CSSProperties}
      >
        <TabsList>
          <TabsTrigger value="documents">{t('documentsAndApprovals')}</TabsTrigger>
          <TabsTrigger value="additionalInfo">{t('additionalInfo')}</TabsTrigger>
          {request.goal?.allowedFor !== AllowedFor.Fund && (
            <TabsTrigger value="supportedDetails">{t('supportedDetails')}</TabsTrigger>
          )}
        </TabsList>
        {request.frozenAmount != null && (
          <div className={styles.frozenBanner}>
            <img src={alertIcon} alt="" className={styles.frozenBannerIcon} />
            <span className="body-sm-regular">
              {t('frozenBannerText', { amount: formatCurrency(request.frozenAmount) })}
            </span>
          </div>
        )}
        <TabsContent value="documents" className={`${styles.content} custom-scrollbar`}>
          <div className="flex flex-col gap-3">
            <h3 className="body-md-semibold text-(--font-heading)">{t('documentsAndApprovals')}</h3>
            <table className="w-full">
              <thead className={styles.thead}>
                <tr>
                  <th className={`${styles.th} body-xs-sm-bold`}>
                    {t('document')}/{t('approval')}
                  </th>
                  <th className={`${styles.th} body-xs-sm-bold`}>{t('uploadDate')}</th>
                  <th className={`${styles.th} body-xs-sm-bold`}>{t('status')}</th>
                  <th className={`${styles.th} body-xs-sm-bold`}>{t('officeComment')}</th>
                  <th className={`${styles.th} body-xs-sm-bold`}>{t('userComment')}</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingDocs ? (
                  <tr>
                    <td colSpan={5}>
                      <div className={styles.loadingDocs}>
                        <p className="body-md-regular">{t('loading')}...</p>
                      </div>
                    </td>
                  </tr>
                ) : documents.length > 0 ? (
                  documents.map((document, index) => (
                    <tr key={`${document.name}-${index}`}>
                      <td className={`body-sm-regular ${styles.td}`}>
                        <div className="flex items-center gap-1">
                          <a target="_blank" rel="noopener noreferrer" href={document.uri} className="text-(--font-link)">
                            {document.name}
                          </a>
                          <DownloadFileButton uri={document.uri} fileName={document.name} />
                        </div>
                      </td>
                      <td className={`body-sm-regular ${styles.td}`}>
                        {formatDate(String(document.creationTime))}
                      </td>
                      <td className={`body-sm-regular ${styles.td}`}>
                        {document.status === DocumentStatus.Rejected ? (
                          <span className="text-(--font-error)">{t('documentStatus.Rejected')}</span>
                        ) : (
                          <span className="text-(--font-body-3)">-</span>
                        )}
                      </td>
                      <td className={`body-sm-regular ${styles.td}`}>
                        {document.officeComment || '-'}
                      </td>
                      <td className={`body-sm-regular ${styles.td}`}>
                        {document.userComment || '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <div className={styles.noDocuments}>
                        <img src={documentsIcon} alt="" />
                        <p className="body-md-regular text-(--font-body-3)">{t('noDocumentsOrApprovalsAttachedToRequest')}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {uploadDocumentsEndpoint && onDocumentsUploaded && (
              <CompletionUploadSection
                // the endpoint is per request; remount so picked files never carry over to another one
                key={uploadDocumentsEndpoint}
                apiEndpoint={uploadDocumentsEndpoint}
                onSuccess={onDocumentsUploaded}
              />
            )}
            {onRequestCompletion && (
              <Button
                variant="outline"
                onClick={onRequestCompletion}
              >
                {t('documents.requestCompletion')}
              </Button>
            )}
          </div>
        </TabsContent>
        <TabsContent value="additionalInfo" className={`${styles.content} custom-scrollbar`}>
          <div className={styles.infoSection}>
            <h3 className="body-md-semibold text-(--font-heading)">{t('requestDetails')}</h3>

            <div className={styles.infoRow}>
              <span className={`body-sm-regular ${styles.infoLabel}`}>{t('fundId')}:</span>
              <span className={`body-sm-medium ${styles.infoValue}`}>{request.fundId}</span>
            </div>

            {request.beneficiaryContact && (
            <div className={styles.infoRow}>
              <span className={`body-sm-regular ${styles.infoLabel}`}>{t('supported')}:</span>
              <span className={`body-sm-medium ${styles.infoValue}`}>
                {request.beneficiaryContact.firstName} {request.beneficiaryContact.lastName}
              </span>
            </div>
            )}

            {request.requestedBecauseOfContact &&
             request.requestedBecauseOfContact.id !== request.beneficiaryContact?.id && (
              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('requestedFor')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>
                  {request.requestedBecauseOfContact.firstName} {request.requestedBecauseOfContact.lastName}
                </span>
              </div>
            )}

            {/* SPR-10012: the child's birth date as entered in the children screen - the office
                checks a bar mitzvah request against it. */}
            {request.requestedBecauseOfContact &&
             request.requestedBecauseOfContact.id !== request.beneficiaryContact?.id &&
             request.requestedBecauseOfContact.birthDate && (
              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('childBirthDate')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>
                  {formatDate(String(request.requestedBecauseOfContact.birthDate))}
                </span>
              </div>
            )}

            {request.requestedBecauseOfContact &&
             request.requestedBecauseOfContact.id !== request.beneficiaryContact?.id &&
             request.requestedBecauseOfContactStatus != null && (
              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('childVerificationStatus')}:</span>
                <span className={`${styles.status} body-sm-medium`}>
                  <span
                    className={styles['status-color']}
                    style={{ '--status-color': statusColorSets[request.requestedBecauseOfContactStatus as RelationshipStatus].border } as CSSProperties}
                  ></span>
                  {t(`childStatus.${request.requestedBecauseOfContactStatus}`)}
                </span>
              </div>
            )}

            <div className={styles.infoRow}>
              <span className={`body-sm-regular ${styles.infoLabel}`}>{t('requestType')}:</span>
              <span className={`body-sm-medium ${styles.infoValue}`}>{getRequestTypeDisplay()}</span>
            </div>

            <div className={styles.infoRow}>
              <span className={`body-sm-regular ${styles.infoLabel}`}>{t('requestedAmount')}:</span>
              <span className={`body-sm-medium ${styles.infoValue}`}>{formatCurrency(request.requestedAmount)}</span>
            </div>

            <div className={styles.infoRow}>
              <span className={`body-sm-regular ${styles.infoLabel}`}>{t('approvedAmount')}:</span>
              <span className={`body-sm-medium ${styles.infoValue}`}>{formatCurrency(request.approvedAmount)}</span>
            </div>

            {request.paymentMethodType != null && (
              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('paymentMethod')}:</span>
                <span className={`body-sm-medium ${styles.infoValue} ${styles.paymentMethodValue}`}>
                  {request.paymentMethodType === PaymentMethodType.BankAccount && (
                    request.bankNumber || request.bankBranchNumber || request.bankAccountNumber
                  ) ? (
                    <>
                      <span className="flex flex-col min-w-0">
                        <span
                          id={`bank-details-${request.multiRequestCorrelationId}`}
                          className={request.paymentInfoStatus === PaymentInfoStatus.Deleted ? styles.deletedPaymentMethod : undefined}
                        >
                          {`בנק ${request.bankNumber || '-'}, סניף ${request.bankBranchNumber || '-'}, חשבון ${request.bankAccountNumber || '-'}.`}
                        </span>
                        {/* HAT-1395: the red alone did not say why, or what happens next. */}
                        {request.paymentInfoDeletedForReturnedPayment && (
                          <span className="body-xs-sm-regular text-(--font-body-2)">{t('returnedPayment.officeBladeDeletedForReturn')}</span>
                        )}
                      </span>
                      {request.paymentInfoStatus === PaymentInfoStatus.Deleted && (
                        <CustomTooltip targetId={`bank-details-${request.multiRequestCorrelationId}`}>
                          {t('deletedBankAccountTooltip')}
                        </CustomTooltip>
                      )}
                    </>
                  ) : (
                    t(request.paymentMethodType)
                  )}
                  {request.paymentMethodType === PaymentMethodType.BankAccount && onChangeBankAccount && (
                    <button 
                      className={styles.editButton} 
                      onClick={onChangeBankAccount}
                      title={t('changeBankAccount')}
                    >
                      <img src={editIcon} alt={t('changeBankAccount')} />
                    </button>
                  )}
                </span>
              </div>
            )}

            <div className={styles.infoRow}>
              <span className={`body-sm-regular ${styles.infoLabel}`}>{t('submissionDate')}:</span>
              <span className={`body-sm-medium ${styles.infoValue}`}>{formatDateTime(request.requestedAt)}</span>
            </div>

            {documentDate && (
              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t(`documentDate.requestLabel.${documentDate.kind}`)}:</span>
                <span className={`body-sm-medium ${styles.infoValue} ${styles.paymentMethodValue}`}>
                  {formatDate(documentDate.date)}
                  {documentDate.isValid != null && (
                    <span
                      className={`${styles.status} body-sm-medium`}
                      title={documentDate.isValid ? undefined : t('documentDate.invalidTitle', { months: DOCUMENT_VALIDITY_MONTHS })}
                    >
                      <span
                        className={styles['status-color']}
                        style={{ '--status-color': `var(--status-${documentDate.isValid ? 'approved' : 'rejected'}-border)` } as CSSProperties}
                      ></span>
                      {t(documentDate.isValid ? 'documentDate.valid' : 'documentDate.invalid')}
                    </span>
                  )}
                </span>
              </div>
            )}

            <div className={styles.infoRow}>
              <span className={`body-sm-regular ${styles.infoLabel}`}>{t('supportDates')}:</span>
              <div className={styles.supportDatesContainer}>
                <span className={`body-sm-medium ${styles.infoValue} ${styles.supportDatesValue}`}>
                  {request.requestedForDates.map((date, index) => (
                    <span key={index}>
                      {formatDate(date)}
                      {index < request.requestedForDates.length - 1 && ', '}
                    </span>
                  ))}
                </span>
                {onEditRequestedForDate && (
                  <button
                    className={styles.editButton}
                    onClick={onEditRequestedForDate}
                    title={t('editRequestedForDate.title')}
                  >
                    <img src={editIcon} alt={t('editRequestedForDate.title')} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {request.goal?.name === 'Kollel' && request.additionalDetails && (() => {
            const kollelDetails = request.additionalDetails as KollelDetails;
            return (
              <>
                <div className={styles.line} />
                <div className={styles.infoSection}>
                  <h3 className="body-md-semibold text-(--font-heading)">{t('kollelDetails')}</h3>

                  {kollelDetails.kollelName && (
                    <div className={styles.infoRow}>
                      <span className={`body-sm-regular ${styles.infoLabel}`}>{t('kollelName')}:</span>
                      <span className={`body-sm-medium ${styles.infoValue}`}>{kollelDetails.kollelName}</span>
                    </div>
                  )}

                  {kollelDetails.roshKollelName && (
                    <div className={styles.infoRow}>
                      <span className={`body-sm-regular ${styles.infoLabel}`}>{t('roshKollelName')}:</span>
                      <span className={`body-sm-medium ${styles.infoValue}`}>{kollelDetails.roshKollelName}</span>
                    </div>
                  )}

                  {kollelDetails.kollelAddress && (
                    <div className={styles.infoRow}>
                      <span className={`body-sm-regular ${styles.infoLabel}`}>{t('kollelAddress')}:</span>
                      <span className={`body-sm-medium ${styles.infoValue}`}>{kollelDetails.kollelAddress}</span>
                    </div>
                  )}

                  {kollelDetails.studyMaterial && (
                    <div className={styles.infoRow}>
                      <span className={`body-sm-regular ${styles.infoLabel}`}>{t('studyMaterial')}:</span>
                      <span className={`body-sm-medium ${styles.infoValue}`}>{kollelDetails.studyMaterial}</span>
                    </div>
                  )}

                  {kollelDetails.studyTimes && kollelDetails.studyTimes.length > 0 && (
                    <div className={styles.infoRow}>
                      <span className={`body-sm-regular ${styles.infoLabel}`}>{t('studyTimes')}:</span>
                      <span className={`body-sm-medium ${styles.infoValue}`}>
                        {kollelDetails.studyTimes.map((time) => t(`StudyTimeOptions.${time}`)).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </>
            );
          })()}

          {request.goal?.allowedFor === AllowedFor.Fund && (request.supplier || request.additionalDetails) && (() => {
            const fundExpenseDetails = (request.additionalDetails as FundExpenseDetails | undefined) ?? ({} as FundExpenseDetails);
            const isAlreadyPaid = fundExpenseDetails.isAlreadyPaid;
            const supplier = request.supplier;
            const supplierDisplay = getSupplierDisplayDetails(supplier, request.additionalDetails);
            const paymentBank = getPaymentBankDetails(
              {
                bankCode: request.bankNumber,
                bankBranch: request.bankBranchNumber,
                bankAccount: request.bankAccountNumber,
              },
              request.additionalDetails,
            );
            const hasWithholding = supplier?.taxWithholdingPercentage != null && supplier.taxWithholdingPercentage > 0;
            const approvalPendingValidUntil = supplier?.taxApprovalStatus === SupplierTaxApprovalStatus.PendingValidUntil;
            const approvalPendingDocument = supplier?.taxApprovalStatus === SupplierTaxApprovalStatus.PendingDocument;
            const approvalExpired = supplier?.taxApprovalStatus === SupplierTaxApprovalStatus.Expired;
            const approvalActionable = approvalPendingValidUntil || approvalPendingDocument || approvalExpired;

            const approvalBannerText = (() => {
              if (approvalExpired) {
                return t('suppliersManagement.banners.approvalExpired', {
                  name: supplier?.name ?? '',
                  date: supplier?.taxApprovalValidUntil
                    ? new Date(supplier.taxApprovalValidUntil).toLocaleDateString('he-IL')
                    : '',
                });
              }
              if (approvalPendingDocument) {
                return t('suppliersManagement.banners.approvalPendingDocument', { name: supplier?.name ?? '' });
              }
              if (approvalPendingValidUntil) {
                return t('suppliersManagement.banners.approvalPendingValidUntil', { name: supplier?.name ?? '' });
              }
              return '';
            })();

            return (
              <>
                {approvalActionable && (
                  <div className={styles.bannerWarning}>
                    <p className="body-sm-semibold">{approvalBannerText}</p>
                    <p className="body-xs-sm-regular">{t('suppliersManagement.banners.approvalIncomplete')}</p>
                  </div>
                )}
                {hasWithholding && !approvalActionable && (
                  <div className={styles.bannerWarning}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="body-sm-semibold">
                        {t('suppliersManagement.banners.withholdingNotice', {
                          name: supplier?.name ?? '',
                          percentage: supplier?.taxWithholdingPercentage,
                        })}
                      </p>
                      {supplier?.taxApprovalDocumentId != null && (
                        <button
                          type="button"
                          className="body-sm-semibold text-(--font-primary) underline hover:opacity-80 shrink-0"
                          onClick={() => openSupplierTaxApprovalDocument(supplier.id, currentUser?.token)}
                        >
                          {t('suppliersManagement.viewDocument')}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className={styles.line} />
                <div className={styles.infoSection}>
                  <div className="flex justify-between items-center">
                    <h3 className="body-md-semibold text-(--font-heading)">{t('fundExpenseDetails')}</h3>
                    {onChangeGoalType && (
                      <Button variant="outline" onClick={onChangeGoalType}>
                        {t('changeGoalType.button')}
                      </Button>
                    )}
                  </div>

                  <div className={styles.infoRow}>
                    <span className={`body-sm-regular ${styles.infoLabel}`}>{t('goal')}:</span>
                    <span className={`body-sm-medium ${styles.infoValue}`}>
                      {t(request.goal?.name || '')}
                    </span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={`body-sm-regular ${styles.infoLabel}`}>{t('paymentStatus')}:</span>
                    <span className={`body-sm-medium ${styles.infoValue}`}>
                      {fundExpenseDetails.isAlreadyPaid ? t('alreadyPaid') : t('notYetPaid')}
                    </span>
                  </div>
                </div>

                <div className={styles.line} />
                <div className={styles.infoSection}>
                  <h3 className="body-md-semibold text-(--font-heading)">{t('supplierDetails')}</h3>

                  <div className={styles.infoRow}>
                    <span className={`body-sm-regular ${styles.infoLabel}`}>{t('supplierName')}:</span>
                    <span className={`body-sm-medium ${styles.infoValue}`}>{supplierDisplay?.name ?? ''}</span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={`body-sm-regular ${styles.infoLabel}`}>{t('supplierTaxId')}:</span>
                    <span className={`body-sm-medium ${styles.infoValue}`}>{supplierDisplay?.taxId ?? ''}</span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={`body-sm-regular ${styles.infoLabel}`}>{t('invoiceNumber')}:</span>
                    <span className={`body-sm-medium ${styles.infoValue}`}>{request.invoiceNumber ?? ''}</span>
                  </div>

                  {!isAlreadyPaid && (
                    <div className={styles.infoRow}>
                      <span className={`body-sm-regular ${styles.infoLabel}`}>{t('bankAccount')}:</span>
                      <span className={`body-sm-medium ${styles.infoValue}`}>
                        {`בנק ${paymentBank?.bankCode || '-'}, סניף ${paymentBank?.bankBranch || '-'}, חשבון ${paymentBank?.bankAccount || '-'}.`}
                      </span>
                    </div>
                  )}
                </div>

                {isAlreadyPaid && (
                  <>
                    <div className={styles.line} />
                    <div className={styles.infoSection}>
                      <h3 className="body-md-semibold text-(--font-heading)">{t('payerDetails')}</h3>

                      {fundExpenseDetails.payerName && (
                        <div className={styles.infoRow}>
                          <span className={`body-sm-regular ${styles.infoLabel}`}>{t('payerName')}:</span>
                          <span className={`body-sm-medium ${styles.infoValue}`}>{fundExpenseDetails.payerName}</span>
                        </div>
                      )}

                      {fundExpenseDetails.payerIsraeliId && (
                        <div className={styles.infoRow}>
                          <span className={`body-sm-regular ${styles.infoLabel}`}>{t('payerIsraeliId')}:</span>
                          <span className={`body-sm-medium ${styles.infoValue}`}>{fundExpenseDetails.payerIsraeliId}</span>
                        </div>
                      )}

                      <div className={styles.infoRow}>
                        <span className={`body-sm-regular ${styles.infoLabel}`}>{t('bankAccount')}:</span>
                        <span className={`body-sm-medium ${styles.infoValue}`}>
                          {`בנק ${paymentBank?.bankCode || '-'}, סניף ${paymentBank?.bankBranch || '-'}, חשבון ${paymentBank?.bankAccount || '-'}.`}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </>
            );
          })()}

          {request.holidayPeriodNames?.length > 0 && (
            <>
              <div className={styles.line} />
              <div className={styles.infoSection}>
                <p className="body-md-medium">
                  {t('holidayInRequestMonth')}: {request.holidayPeriodNames.join(', ')}
                </p>
              </div>
            </>
          )}

          {(() => {
            const autoComments = request.officeComments?.filter(c => AUTO_OFFICE_COMMENTS_VALUES.includes(c.comment)) || [];
            const hasContent = request.userComments?.length > 0 || autoComments.length > 0 || onOpenOfficeComments;

            return hasContent && (
              <>
                <div className={styles.line} />
                <div className={styles.infoSection}>
                  <h3 className="body-md-semibold text-(--font-heading)">{t('comments')}</h3>

                  {request.userComments?.length > 0 && (
                    <div className={styles.infoRow}>
                      <span className={`body-sm-regular ${styles.infoLabel}`}>{t('userComments')}:</span>
                      <span className={`body-sm-medium ${styles.infoValue}`}>
                        {request.userComments.map((comment, index) => (
                          <span key={index}>
                            {comment}
                            {index < request.userComments.length - 1 && <br />}
                          </span>
                        ))}
                      </span>
                    </div>
                  )}

                  {onOpenOfficeComments && (
                    <div className={styles.infoRow}>
                      <span className={`body-sm-semibold ${styles.infoLabel}`} style={{ color: 'var(--font-heading)' }}>{t('officeComments.title')}:</span>
                      <button className={styles.officeCommentsButton} onClick={onOpenOfficeComments}>
                        {t('officeComments.manageComments')}
                        {officeCommentsCount > 0 && <span className={styles.officeCommentsBadge}>{officeCommentsCount}</span>}
                      </button>
                    </div>
                  )}

                  {autoComments.length > 0 && (
                    <div className={styles.infoRow}>
                      <span className={`body-sm-regular ${styles.infoLabel}`}>{t('systemNotes')}:</span>
                      <span className={`body-sm-medium ${styles.infoValue}`}>
                        {autoComments.map((oc, index) => (
                          <span key={index}>
                            {oc.comment}
                            {index < autoComments.length - 1 && <br />}
                          </span>
                        ))}
                      </span>
                    </div>
                  )}
                </div>
              </>
            );
          })()}

          {(isLoadingRelatedSupportRequests || relatedSupportRequests !== undefined) && (
            <>
              <div className={styles.line} />
              <div className={styles.infoSection}>
                <h3 className="body-md-semibold text-(--font-heading)">{t('relatedSupportRequests.title')}</h3>
                {isLoadingRelatedSupportRequests ? (
                  <p className="body-sm-regular text-(--font-body-3)">{t('loading')}...</p>
                ) : relatedSupportRequests && relatedSupportRequests.length > 0 ? (
                  <table className="w-full">
                    <thead className={styles.thead}>
                      <tr>
                        <th className={`${styles.th} body-xs-sm-bold`}>{t('fund')}</th>
                        <th className={`${styles.th} body-xs-sm-bold`}>{t('status')}</th>
                        <th className={`${styles.th} body-xs-sm-bold`}>{t('approvedAmount')}</th>
                        <th className={`${styles.th} body-xs-sm-bold`}>{t('supportDate')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relatedSupportRequests.map((req, index) => (
                        <tr key={`${req.correlationId}-${req.counter ?? index}`}>
                          <td className={`body-sm-regular ${styles.td}`}>{req.fundId}</td>
                          <td className={`body-sm-regular ${styles.td}`}>{t(req.status)}</td>
                          <td className={`body-sm-regular ${styles.td}`}>
                            {req.approvedAmount.toLocaleString(i18n.language, { style: 'currency', currency: 'ILS' })}
                          </td>
                          <td className={`body-sm-regular ${styles.td}`}>{formatDate(req.requestedForDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="body-sm-regular text-(--font-body-3)">{t('relatedSupportRequests.noRequests')}</p>
                )}
              </div>
            </>
          )}
        </TabsContent>
        {request.goal?.allowedFor !== AllowedFor.Fund && (
          <TabsContent value="supportedDetails" className={`${styles.content} custom-scrollbar`}>
            {request.beneficiaryContact ? (<>
          <div className={styles.infoSection}>
            <div className="flex justify-between items-center">
              <h3 className="body-md-semibold text-(--font-heading)">{t('supportedDetails')}</h3>
              {onConvertToFundExpense && (
                <Button variant="outline" onClick={onConvertToFundExpense}>
                  {t('convertToFundExpense.button')}
                </Button>
              )}
            </div>

            <div className={styles.infoRow}>
              <span className={`body-sm-regular ${styles.infoLabel}`}>{t('fullName')}:</span>
              <span className={`body-sm-medium ${styles.infoValue}`}>
                {request.beneficiaryContact.firstName} {request.beneficiaryContact.lastName}
              </span>
            </div>

            <div className={styles.infoRow}>
              <span className={`body-sm-regular ${styles.infoLabel}`}>{t('israeliId')}:</span>
              <span className={`body-sm-medium ${styles.infoValue}`}>{request.beneficiaryContact.israeliId}</span>
            </div>

            {request.beneficiaryContact.phone && (
              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('phoneNumber')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>{request.beneficiaryContact.phone}</span>
              </div>
            )}

            {request.beneficiaryContact.email && (
              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('email')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>{request.beneficiaryContact.email}</span>
              </div>
            )}

            {request.beneficiaryContact.birthDate && (
              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('birthDate')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>
                  {formatDate(String(request.beneficiaryContact.birthDate))}
                </span>
              </div>
            )}
          </div>

          {request.beneficiaryContact.address && (
            request.beneficiaryContact.address.city ||
            request.beneficiaryContact.address.streetName
          ) && (
            <>
              <div className={styles.line} />
              <div className={styles.infoSection}>
                <h3 className="body-md-semibold text-(--font-heading)">{t('address')}</h3>

                {(request.beneficiaryContact.address.streetName || request.beneficiaryContact.address.streetNumber) && (
                  <div className={styles.infoRow}>
                    <span className={`body-sm-regular ${styles.infoLabel}`}>{t('street')}:</span>
                    <span className={`body-sm-medium ${styles.infoValue}`}>
                      {request.beneficiaryContact.address.streetName} {request.beneficiaryContact.address.streetNumber}
                      {request.beneficiaryContact.address.apartmentNumber && ` / ${request.beneficiaryContact.address.apartmentNumber}`}
                    </span>
                  </div>
                )}

                {request.beneficiaryContact.address.city && (
                  <div className={styles.infoRow}>
                    <span className={`body-sm-regular ${styles.infoLabel}`}>{t('city')}:</span>
                    <span className={`body-sm-medium ${styles.infoValue}`}>{request.beneficiaryContact.address.city}</span>
                  </div>
                )}

                {request.beneficiaryContact.address.zipCode && (
                  <div className={styles.infoRow}>
                    <span className={`body-sm-regular ${styles.infoLabel}`}>{t('zipCode')}:</span>
                    <span className={`body-sm-medium ${styles.infoValue}`}>{request.beneficiaryContact.address.zipCode}</span>
                  </div>
                )}

                {request.beneficiaryContact.address.country && (
                  <div className={styles.infoRow}>
                    <span className={`body-sm-regular ${styles.infoLabel}`}>{t('country')}:</span>
                    <span className={`body-sm-medium ${styles.infoValue}`}>{request.beneficiaryContact.address.country}</span>
                  </div>
                )}
              </div>
            </>
          )}
          </>) : null}

          <div className={styles.line} />
          <div className={styles.infoSection}>
            <h3 className="body-md-semibold text-(--font-heading)">{t('classifications')}</h3>

            {request.beneficiaryContactClassifications && request.beneficiaryContactClassifications.length > 0 ? (
              <div className={styles.classificationsList}>
                {request.beneficiaryContactClassifications.map((cc) => {
                  const expirationStatus = getExpirationStatus(cc);
                  const statusColor = getExpirationStatusColor(expirationStatus);
                  const expiryText = cc.expiresTimestamp
                    ? `${t('valid')} ${new Date(cc.expiresTimestamp).toLocaleDateString('he-IL')}`
                    : '';

                  return (
                    <div key={cc.id} className={styles.classificationCard}>
                      <div className={styles.classificationHeader}>
                        <span className="body-md-bold text-(--font-sub-title-2)">
                          {t(cc.classification.name)}
                        </span>
                        {expiryText && (
                          <>
                            <div className={styles.headerDivider} />
                            <span className="body-md-semibold">{expiryText}</span>
                          </>
                        )}
                        <div className={styles.headerDivider} />
                        <div className={styles.classificationStatusBadge}>
                          <span
                            className={styles.classificationColorDot}
                            style={{ '--classification-color': statusColor } as CSSProperties}
                          />
                          <span className="body-md-medium">
                            {expirationStatus ? t(`ExpirationStatus.${expirationStatus}`) : t(cc.status)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="body-sm-regular text-(--font-body-3)">{t('noClassifications')}</p>
            )}
          </div>
          </TabsContent>
        )}
      </Tabs>
    </Blade>
  );
};

export default AdditionalInfo;



